import React, { useEffect, useRef, useState, useCallback } from "react"

const PictureInPictureOverlay = ({
  isCameraOn = false,
  isScreenSharing = false,
  // Separate LiveKit props for camera and screen
  localCameraTrack = null,
  localScreenTrack = null,
  remoteLiveKitTracks = {},
  localCameraMetadata = null,
  localScreenMetadata = null,
  remoteTrackMetadata = {},
}) => {
  const [isTabHidden, setIsTabHidden] = useState(false)
  const [pipActive, setPipActive] = useState(false)
  const hiddenVideoRef = useRef(null)
  const currentTrackRef = useRef(null)
  const pipTimeoutRef = useRef(null)

  // More robust tab detection with state tracking
  useEffect(() => {
    let focusState = !document.hidden

    const handleVisibilityChange = () => {
      const hidden = document.hidden
      // Only update if state actually changed
      if (hidden !== !focusState) {
        focusState = !hidden
        setIsTabHidden(hidden)
      }
    }

    const handleFocus = () => {
      if (!focusState) {
        focusState = true
        setIsTabHidden(false)
      }
    }

    const handleBlur = () => {
      if (focusState) {
        focusState = false
        setIsTabHidden(true)
      }
    }

    // Use both visibility API and focus/blur for better detection
    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.addEventListener("focus", handleFocus)
    window.addEventListener("blur", handleBlur)

    // Set initial state
    setIsTabHidden(document.hidden)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("focus", handleFocus)
      window.removeEventListener("blur", handleBlur)
      if (pipTimeoutRef.current) {
        clearTimeout(pipTimeoutRef.current)
      }
    }
  }, [])

  // In PictureInPictureOverlay.jsx, replace the getBestTrackAndStream function:

  const getBestTrackAndStream = useCallback(() => {
    const remoteTrackEntries = Object.entries(remoteLiveKitTracks)

    // Priority 1: Remote screen share tracks
    for (const [participantId, livekitTrack] of remoteTrackEntries) {
      if (
        livekitTrack?.mediaStreamTrack &&
        livekitTrack.mediaStreamTrack.kind === "video" &&
        livekitTrack.mediaStreamTrack.readyState === "live" &&
        livekitTrack.mediaStreamTrack.enabled
      ) {
        const metadata = remoteTrackMetadata[participantId]
        // Prioritize screen shares from remote participants
        if (
          metadata?.enabled &&
          !metadata?.muted &&
          metadata?.isScreenSharing
        ) {
          // Create MediaStream from LiveKit track
          const stream = new MediaStream([livekitTrack.mediaStreamTrack])
          return {
            track: livekitTrack,
            stream,
            participantId,
            type: "remote-screen",
          }
        }
      }
    }

    // Priority 2: Remote camera feeds
    for (const [participantId, livekitTrack] of remoteTrackEntries) {
      if (
        livekitTrack?.mediaStreamTrack &&
        livekitTrack.mediaStreamTrack.kind === "video" &&
        livekitTrack.mediaStreamTrack.readyState === "live" &&
        livekitTrack.mediaStreamTrack.enabled
      ) {
        const metadata = remoteTrackMetadata[participantId]
        // Camera feeds from remote participants
        if (
          metadata?.enabled &&
          !metadata?.muted &&
          !metadata?.isScreenSharing
        ) {

          // Create MediaStream from LiveKit track
          const stream = new MediaStream([livekitTrack.mediaStreamTrack])
          return {
            track: livekitTrack,
            stream,
            participantId,
            type: "remote-camera",
          }
        }
      }
    }

    // Priority 3: Local camera track
    if (
      localCameraTrack?.mediaStreamTrack &&
      localCameraTrack.mediaStreamTrack.kind === "video" &&
      localCameraTrack.mediaStreamTrack.readyState === "live" &&
      localCameraTrack.mediaStreamTrack.enabled &&
      isCameraOn
    ) {
      if (localCameraMetadata?.enabled && !localCameraMetadata?.muted) {

        // Create MediaStream from LiveKit track
        const stream = new MediaStream([localCameraTrack.mediaStreamTrack])
        return {
          track: localCameraTrack,
          stream,
          participantId: "local-camera",
          type: "local-camera",
        }
      }
    }

    // Priority 4: Local screen share track (fallback)
    if (
      localScreenTrack?.mediaStreamTrack &&
      localScreenTrack.mediaStreamTrack.kind === "video" &&
      localScreenTrack.mediaStreamTrack.readyState === "live" &&
      localScreenTrack.mediaStreamTrack.enabled &&
      isScreenSharing
    ) {
      if (localScreenMetadata?.enabled && !localScreenMetadata?.muted) {

        // Create MediaStream from LiveKit track
        const stream = new MediaStream([localScreenTrack.mediaStreamTrack])
        return {
          track: localScreenTrack,
          stream,
          participantId: "local-screen",
          type: "local-screen",
        }
      }
    }

    return null
  }, [
    localCameraTrack,
    localScreenTrack,
    remoteLiveKitTracks,
    localCameraMetadata,
    localScreenMetadata,
    remoteTrackMetadata,
    isCameraOn,
    isScreenSharing,
  ])

  // Handle PiP activation with better error handling
  const activatePiP = useCallback(async () => {
    if (!hiddenVideoRef.current || pipActive) return

    try {
      const trackInfo = getBestTrackAndStream()
      if (!trackInfo) {
        return
      }

      const { track, stream, participantId, type } = trackInfo

      // Exit any existing PiP first
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture()
        // Small delay to ensure clean exit
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      const video = hiddenVideoRef.current

      // Set the new stream from LiveKit track
      if (currentTrackRef.current !== track) {
        video.srcObject = stream
        currentTrackRef.current = track

        // Wait for video to load properly
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(
            () => reject(new Error("Video load timeout")),
            3000
          )

          const handleLoad = () => {
            clearTimeout(timeout)
            video.removeEventListener("loadedmetadata", handleLoad)
            video.removeEventListener("error", handleError)
            resolve()
          }

          const handleError = (e) => {
            clearTimeout(timeout)
            video.removeEventListener("loadedmetadata", handleLoad)
            video.removeEventListener("error", handleError)
            reject(e)
          }

          video.addEventListener("loadedmetadata", handleLoad)
          video.addEventListener("error", handleError)

          // If already loaded, resolve immediately
          if (video.readyState >= 1) {
            clearTimeout(timeout)
            resolve()
          }
        })
      }

      // Ensure video is playing
      if (video.paused) {
        await video.play()
      }

      // Request PiP
      await video.requestPictureInPicture()
      setPipActive(true)

      // Listen for track ended event
      const handleTrackEnded = () => {
        deactivatePiP()
      }

      if (track.mediaStreamTrack) {
        track.mediaStreamTrack.addEventListener("ended", handleTrackEnded)
      }
    } catch (error) {
      console.error("PiP: Activation failed:", error.message)
      setPipActive(false)
    }
  }, [getBestTrackAndStream, pipActive])

  // Handle PiP deactivation
  const deactivatePiP = useCallback(async () => {
    if (!pipActive) return

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture()
      }
      setPipActive(false)
      currentTrackRef.current = null
    } catch (error) {
      console.error("PiP: Deactivation failed:", error.message)
      setPipActive(false)
    }
  }, [pipActive])

  // Handle tab visibility changes with debouncing
  useEffect(() => {
    if (pipTimeoutRef.current) {
      clearTimeout(pipTimeoutRef.current)
    }

    pipTimeoutRef.current = setTimeout(() => {
      if (isTabHidden && !pipActive) {
        activatePiP()
      } else if (!isTabHidden && pipActive) {
        deactivatePiP()
      }
    }, 300) // Small delay to avoid rapid toggling

    return () => {
      if (pipTimeoutRef.current) {
        clearTimeout(pipTimeoutRef.current)
      }
    }
  }, [isTabHidden, pipActive, activatePiP, deactivatePiP])

  // Listen for PiP events
  useEffect(() => {
    if (!hiddenVideoRef.current) return

    const video = hiddenVideoRef.current

    const handleEnterPip = () => {
      setPipActive(true)
    }

    const handleLeavePip = () => {
      setPipActive(false)
      currentTrackRef.current = null
    }

    video.addEventListener("enterpictureinpicture", handleEnterPip)
    video.addEventListener("leavepictureinpicture", handleLeavePip)

    return () => {
      video.removeEventListener("enterpictureinpicture", handleEnterPip)
      video.removeEventListener("leavepictureinpicture", handleLeavePip)
    }
  }, [])

  // Update track when LiveKit tracks change
  useEffect(() => {
    if (pipActive && hiddenVideoRef.current) {
      const trackInfo = getBestTrackAndStream()
      if (trackInfo && trackInfo.track !== currentTrackRef.current) {
        hiddenVideoRef.current.srcObject = trackInfo.stream
        currentTrackRef.current = trackInfo.track
      }
    }
  }, [
    localCameraTrack,
    localScreenTrack,
    remoteLiveKitTracks,
    localCameraMetadata,
    localScreenMetadata,
    remoteTrackMetadata,
    isCameraOn,
    isScreenSharing,
    pipActive,
    getBestTrackAndStream,
  ])

  return (
    <>
      {/* Hidden video element for PiP */}
      <video
        ref={hiddenVideoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "fixed",
          top: "-200px",
          left: "-200px",
          width: "100px",
          height: "100px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      />
    </>
  )
}

export default PictureInPictureOverlay